import { useState, useEffect, useMemo } from "react";
import { Smartphone, Battery, BatteryCharging, MapPin, RefreshCw, Clock, ShieldCheck, Cloud, Building2, Activity, UserCheck, ChevronRight } from "lucide-react";
import { useAuthStore } from "../../state/auth";
import { cn } from "../../lib/utils";
import { GeofenceManager } from "../../components/geofence/GeofenceManager";
import { ViolationsList } from "../../components/geofence/ViolationsList";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { useTranslation } from "../../i18n";

interface Device {
    id: string;
    serial_number: string;
    hospital_id: string;
    hospital_name: string;
    status: string;
    battery_level: number | null;
    is_charging: boolean | null;
    gps_lat: number | null;
    gps_lng: number | null;
    app_version: string | null;
    last_seen_at: string | null;
    // Real usage, independent of device telemetry (see /api/devices): a handset
    // officers scan with is "in use" even if its device account never POSTed
    // battery/GPS. Populated server-side.
    last_scan_at?: string | null;
    last_signon_at?: string | null;
    scans_24h?: number | null;
}

type TabType = "devices" | "geofences" | "violations";

// Known hospital building centers (lat, lng). Mirrors HOSPITAL_CENTERS in
// ScanMapPage.tsx — derived from the average GPS position of recorded scans at
// each site. Used to snap drifting device GPS back toward the building.
const HOSPITAL_CENTERS: Array<[number, number]> = [
    [33.82843623, -84.33483732], // Emory Musculoskeletal Institute
    [33.83394786, -84.23074006], // Northlake
    [34.06672631, -84.17660777], // Emory Johns Creek Hospital
    [33.79420537, -84.27944156], // Executive Park 12
    [33.67155397, -84.10960765], // Emory University Hospital (EUH)
    [33.79229569, -84.32005190], // The Emory Clinics: A, B & C
    [33.79114926, -84.32042330], // Emory University Hospital Clifton Tower
    [33.90850845, -84.34952764], // Emory Saint Joseph Hospital
    [33.73714753, -84.49153397], // Emory Hillandale Hospital
];

// "Snap to building" tuning, matching ScanMapPage.tsx. Indoor GPS can drift
// 10-50m; a device within SNAP_RADIUS_M of the nearest hospital center has its
// offset compressed to SNAP_FACTOR (35% kept => ~65% pulled toward the center).
const SNAP_RADIUS_M = 100;
const SNAP_FACTOR = 0.35;

/**
 * Snaps device GPS toward the nearest hospital center to counter indoor GPS
 * drift. If the nearest center is within SNAP_RADIUS_M, the device's offset from
 * it is scaled by SNAP_FACTOR; otherwise the coordinates are returned unchanged.
 * Pure display transform — never mutates the underlying telemetry.
 */
function snapDeviceToBuilding(lat: number, lng: number): [number, number] {
    let nearest: [number, number] | null = null;
    let nearestDistM = Infinity;
    for (const [cLat, cLng] of HOSPITAL_CENTERS) {
        // Equirectangular approximation — accurate enough at the ~100m scale.
        const metersPerDegLat = 111_320;
        const metersPerDegLng = 111_320 * Math.cos((cLat * Math.PI) / 180);
        const dLatM = (lat - cLat) * metersPerDegLat;
        const dLngM = (lng - cLng) * metersPerDegLng;
        const distM = Math.hypot(dLatM, dLngM);
        if (distM < nearestDistM) {
            nearestDistM = distM;
            nearest = [cLat, cLng];
        }
    }
    if (!nearest || nearestDistM > SNAP_RADIUS_M) return [lat, lng];
    const [cLat, cLng] = nearest;
    return [cLat + (lat - cLat) * SNAP_FACTOR, cLng + (lng - cLng) * SNAP_FACTOR];
}

// Usage state, shared by the summary tiles and each row. Mutually exclusive,
// checked in this order:
//   in use       scanned with or signed on to in the last 10 minutes
//   online       scanned with or signed on to in the last 24 hours
//   provisioned  never reported, never scanned, never signed on
//   offline      everything else (has history, nothing in the last 24h)
//
// Both "in use" and "online" are driven by real activity (scans, officer
// sign-ons), not device telemetry: last_seen_at is also written by the
// Scalefusion sync and telemetry pings, so a powered-on handset sitting in a
// drawer would otherwise read as busy.
const DAY_MS = 24 * 60 * 60 * 1000;
const IN_USE_MS = 10 * 60 * 1000;
function withinMs(ts: string | null | undefined, ms: number): boolean {
    if (!ts) return false;
    const t = new Date(ts).getTime();
    return Number.isFinite(t) && Date.now() - t < ms;
}
function classifyDevice(d: Device) {
    const hasTelemetry = !!d.last_seen_at;
    const inUse = withinMs(d.last_scan_at, IN_USE_MS) || withinMs(d.last_signon_at, IN_USE_MS);
    const online =
        !inUse &&
        ((d.scans_24h ?? 0) > 0 || withinMs(d.last_scan_at, DAY_MS) || withinMs(d.last_signon_at, DAY_MS));
    const neverUsed = !hasTelemetry && !d.last_scan_at && !d.last_signon_at;
    return { hasTelemetry, online, inUse, neverUsed };
}

export function DevicesPage() {
    const { t, lang } = useTranslation();
    const { user } = useAuthStore();
    const [devices, setDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>("devices");
    // Shared, persisted hospital selection — a multi-site account that narrows to
    // one hospital elsewhere keeps that view here too, until they change it. The
    // list itself is already RBAC-scoped server-side; this filters it client-side.
    const [selectedHospitalId, setSelectedHospitalId] = useStickyHospital();

    const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";

    const visibleDevices = selectedHospitalId
        ? devices.filter((d) => d.hospital_id === selectedHospitalId)
        : devices;

    // Group devices by hospital (like the Checkpoint Directory), sorted by name.
    const devicesByHospital = useMemo(() => {
        const m = new Map<string, Device[]>();
        for (const d of visibleDevices) {
            const k = d.hospital_name || "Unassigned";
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(d);
        }
        return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [visibleDevices]);

    // Fleet summary across the visible devices — mirrors the Checkpoint
    // Directory's stat row, but for device usage state.
    const summary = useMemo(() => {
        let online = 0;
        let inUse = 0;
        let provisioned = 0;
        for (const d of visibleDevices) {
            const c = classifyDevice(d);
            // "Online (last 24h)" includes the handsets in use right now — they
            // were used in the last 24h too. Row badges stay exclusive.
            if (c.inUse) { inUse++; online++; }
            else if (c.online) online++;
            else if (c.neverUsed) provisioned++;
        }
        return { total: visibleDevices.length, hospitals: devicesByHospital.length, online, inUse, provisioned };
    }, [visibleDevices, devicesByHospital.length]);

    // Collapsible hospital sections, collapsed by default — click a hospital to
    // reveal its devices, like the Checkpoint Directory. A single hospital in
    // scope opens automatically (there's nothing to choose between).
    const [openHospitals, setOpenHospitals] = useState<Set<string>>(new Set());
    const hospitalKeys = devicesByHospital.map(([h]) => h);
    // Keyed on the actual set of hospital names so the 30s device refresh (same
    // names, new array) never collapses a section the user just opened; it resets
    // only when the hospital set really changes (filter change / first load).
    const hospitalKeysStr = hospitalKeys.join("|");
    useEffect(() => {
        setOpenHospitals(hospitalKeys.length === 1 ? new Set(hospitalKeys) : new Set());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hospitalKeysStr]);
    const toggleHospital = (h: string) =>
        setOpenHospitals((prev) => {
            const next = new Set(prev);
            next.has(h) ? next.delete(h) : next.add(h);
            return next;
        });

    const fetchDevices = async () => {
        if (!user) return;
        setRefreshing(true);
        try {
            // Devices are scoped server-side to the caller's assigned hospitals:
            // ADMIN "Manager" + SUPERVISOR via user_hospitals, DIRECTOR via their
            // main site, and only an unscoped super-admin sees all. We intentionally
            // do NOT pin a hospital_id here — this page has no per-hospital filter,
            // and pinning the user's single primary hospital would hide devices at
            // their other assigned hospitals.
            const params = new URLSearchParams();
            const headers: Record<string, string> = {
                "Accept": "application/json",
                "x-user-email": user.email,
            };
            const token = localStorage.getItem("authToken");
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }
            const response = await fetch(`/api/devices?${params.toString()}`, { headers });
            if (response.ok) {
                const data = await response.json();
                setDevices(data);
            }
        } catch (error) {
            console.error("Failed to fetch devices:", error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Pull live device telemetry from Scalefusion MDM, then refresh the list.
    // Admin-only — the endpoint itself also enforces the ADMIN role.
    // `force` bypasses the server-side cache: the manual button forces a fresh
    // pull, while the periodic auto-sync lets the cache absorb redundant calls.
    const syncFromScalefusion = async ({ force = false }: { force?: boolean } = {}) => {
        if (!user || syncing) return;
        setSyncing(true);
        setSyncMsg(null);
        try {
            const headers: Record<string, string> = {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "x-user-email": user.email,
            };
            const token = localStorage.getItem("authToken");
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }
            const url = force
                ? "/api/devices/sync-scalefusion?force=true"
                : "/api/devices/sync-scalefusion";
            const response = await fetch(url, {
                method: "POST",
                headers,
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setSyncMsg(
                    t('devices.syncSuccess', 'Synced {n} device(s) from Scalefusion')
                        .replace('{n}', String(data.matched ?? 0))
                );
                await fetchDevices();
            } else {
                setSyncMsg(data.error || t('devices.syncFailed', 'Scalefusion sync failed'));
            }
        } catch (error) {
            console.error("Failed to sync from Scalefusion:", error);
            setSyncMsg(t('devices.syncFailed', 'Scalefusion sync failed'));
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        fetchDevices();
        const interval = setInterval(fetchDevices, 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, [user]);

    // Auto-sync from Scalefusion on load for admins, debounced to at most once
    // per 60 minutes (across reloads) to conserve API quota. The manual "Sync
    // from Scalefusion" button still forces a fresh pull on demand.
    useEffect(() => {
        if (!user || !isAdmin) return;
        const last = Number(localStorage.getItem("sfLastSync") || 0);
        if (Date.now() - last > 60 * 60 * 1000) {
            localStorage.setItem("sfLastSync", String(Date.now()));
            syncFromScalefusion();
        }
    }, [user, isAdmin]);

    if (loading && activeTab === "devices") {
        return (
            <div className="flex h-full items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6 max-w-[1400px] mx-auto">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="flex items-center gap-2.5 text-2xl font-bold text-gray-900 dark:text-white">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#3B7BBE]/10 text-[#3B7BBE]">
                            <Smartphone className="h-5 w-5" />
                        </span>
                        {t('devices.title', 'Connected Patrol Devices')}
                    </h1>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('devices.subtitle', 'Status of every scanning device in the field, grouped by hospital')}</p>
                </div>
                {activeTab === "devices" && (
                    <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                            <HospitalSelector
                                selectedHospitalId={selectedHospitalId}
                                onHospitalChange={setSelectedHospitalId}
                            />
                            {isAdmin && (
                                <button
                                    onClick={() => syncFromScalefusion({ force: true })}
                                    disabled={syncing}
                                    className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
                                >
                                    <Cloud className={cn("h-4 w-4", syncing && "animate-pulse")} />
                                    {syncing
                                        ? t('devices.syncing', 'Syncing…')
                                        : t('devices.syncScalefusion', 'Sync from Scalefusion')}
                                </button>
                            )}
                            <button
                                onClick={fetchDevices}
                                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 dark:border-[#33334d] dark:bg-[#24243e] dark:text-gray-200 dark:hover:bg-[#2a2a47]"
                            >
                                <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                                {t('devices.refreshStatus', 'Refresh Status')}
                            </button>
                        </div>
                        {syncMsg && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{syncMsg}</span>
                        )}
                    </div>
                )}
            </header>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 dark:border-[#33334d]">
                <button
                    onClick={() => setActiveTab("devices")}
                    className={cn(
                        "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                        activeTab === "devices"
                            ? "border-blue-600 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    )}
                >
                    {t('domain.devices', 'Devices')}
                </button>
                <button
                    onClick={() => setActiveTab("geofences")}
                    className={cn(
                        "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                        activeTab === "geofences"
                            ? "border-blue-600 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    )}
                >
                    {t('devices.geofences', 'Geofences')}
                </button>
                <button
                    onClick={() => setActiveTab("violations")}
                    className={cn(
                        "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                        activeTab === "violations"
                            ? "border-blue-600 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    )}
                >
                    {t('devices.violations', 'Violations')}
                </button>
            </div>

            {/* Summary tiles — parallel to the Checkpoint Directory's stat row. */}
            {activeTab === "devices" && summary.total > 0 && (
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatTile icon={<Smartphone className="h-4 w-4" />} label={t('devices.statDevices', 'Devices')} value={summary.total.toLocaleString(lang)} />
                    <StatTile icon={<Building2 className="h-4 w-4" />} label={t('devices.statHospitals', 'Hospitals')} value={summary.hospitals.toLocaleString(lang)} />
                    <StatTile icon={<Activity className="h-4 w-4" />} label={t('devices.statOnline24h', 'Online (last 24h)')} value={summary.online.toLocaleString(lang)} accent="emerald" />
                    <StatTile icon={<UserCheck className="h-4 w-4" />} label={t('devices.statInUseNow', 'In use now')} value={summary.inUse.toLocaleString(lang)} accent="blue" />
                </div>
            )}

            {/* Tab Content — devices grouped by hospital, listed as rows. */}
            {activeTab === "devices" && (
                visibleDevices.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 p-12 text-center dark:border-[#33334d]">
                        <Smartphone className="h-12 w-12 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('devices.noDevicesFound', 'No devices found')}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('devices.emptyHint', 'Connected devices will appear here once they send telemetry.')}</p>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {devicesByHospital.map(([hospital, list]) => {
                            let onlineN = 0;
                            let inUseN = 0;
                            for (const d of list) {
                                const c = classifyDevice(d);
                                if (c.inUse) { inUseN++; onlineN++; }
                                else if (c.online) onlineN++;
                            }
                            const isOpen = openHospitals.has(hospital);
                            return (
                                <section
                                    key={hospital}
                                    className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-[#33334d] dark:bg-[#24243e]"
                                >
                                    {/* Hospital header — click to expand/collapse this hospital's
                                        devices, like the Checkpoint Directory's drill-down. */}
                                    <button
                                        type="button"
                                        onClick={() => toggleHospital(hospital)}
                                        aria-expanded={isOpen}
                                        className={cn(
                                            "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-[#2a2a47]",
                                            isOpen && "border-b border-gray-100 dark:border-[#33334d]"
                                        )}
                                    >
                                        <ChevronRight
                                            className={cn(
                                                "h-4 w-4 flex-shrink-0 text-gray-400 transition-transform",
                                                isOpen && "rotate-90 text-[#3B7BBE]"
                                            )}
                                        />
                                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#3B7BBE]/10 text-[#3B7BBE]">
                                            <Building2 className="h-5 w-5" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate font-semibold text-gray-900 dark:text-white">{hospital}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {t('devices.nDevices', '{n} devices').replace('{n}', String(list.length))}
                                                {onlineN > 0 && <span className="text-emerald-600 dark:text-emerald-400"> · {t('devices.nOnline', '{n} online').replace('{n}', String(onlineN))}</span>}
                                                {inUseN > 0 && <span className="text-[#3B7BBE]"> · {t('devices.nInUse', '{n} in use').replace('{n}', String(inUseN))}</span>}
                                            </div>
                                        </div>
                                        <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-600 dark:bg-[#33334d] dark:text-gray-300">
                                            {list.length}
                                        </span>
                                    </button>
                                    {/* Device list — one row per device, shown when expanded. */}
                                    {isOpen && (
                                        <div className="divide-y divide-gray-100 dark:divide-[#2a2a47]">
                                            {list
                                                .slice()
                                                .sort((a, b) => a.serial_number.localeCompare(b.serial_number))
                                                .map((device) => (
                                                    <DeviceRow key={device.id} device={device} />
                                                ))}
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                )
            )}

            {/* Attribution — shown only once real Scalefusion telemetry is present
                (i.e. at least one device has reported), per Scalefusion API terms. */}
            {activeTab === "devices" && devices.some((d) => d.last_seen_at) && (
                <div className="flex justify-center pt-2">
                    <a
                        href="https://scalefusion.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <Cloud className="h-3 w-3" />
                        {t('devices.poweredByScalefusion', 'Powered by Scalefusion')}
                    </a>
                </div>
            )}

            {activeTab === "geofences" && <GeofenceManager />}

            {activeTab === "violations" && <ViolationsList />}
        </div>
    );
}

/** One device, rendered as a horizontal list row (distinct from the Checkpoint
 *  Directory's square cards / leaf table). Identity + status on the left,
 *  inline metric chips in the middle, a locate action on the right. */
function DeviceRow({ device }: { device: Device }) {
    const { t, lang } = useTranslation();

    const { online, inUse, neverUsed } = classifyDevice(device);
    const scans24 = device.scans_24h || 0;
    const battery = device.battery_level;

    const activityTimes = [device.last_seen_at, device.last_scan_at, device.last_signon_at]
        .filter(Boolean)
        .map((d) => new Date(d as string).getTime());
    const lastActivity = activityTimes.length ? new Date(Math.max(...activityTimes)) : null;

    // Snap GPS toward the nearest hospital center so indoor drift doesn't render
    // the device outside its building on the map. Pure display transform.
    const hasGps = device.gps_lat != null && device.gps_lng != null;
    const [displayLat, displayLng] = hasGps
        ? snapDeviceToBuilding(device.gps_lat as number, device.gps_lng as number)
        : [null, null];

    const badge = inUse
        ? { label: t('devices.inUse', 'In use'), pill: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300", icon: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300" }
        : online
        ? { label: t('status.online', 'Online'), pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", icon: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300" }
        : neverUsed
        ? { label: t('devices.provisioned', 'Provisioned'), pill: "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300", icon: "bg-slate-50 text-slate-500 dark:bg-slate-500/15 dark:text-slate-300" }
        : { label: t('status.offline', 'Offline'), pill: "bg-gray-100 text-gray-500 dark:bg-[#33334d] dark:text-gray-400", icon: "bg-gray-50 text-gray-400 dark:bg-[#33334d] dark:text-gray-500" };

    return (
        <div className="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-[#3B7BBE]/5 dark:hover:bg-[#2a2a47] sm:flex-row sm:items-center">
            {/* identity + status */}
            <div className="flex items-center gap-3 sm:w-56 sm:flex-shrink-0">
                <span className={cn("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg", badge.icon)}>
                    <Smartphone className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                    <div className="truncate font-bold text-gray-900 dark:text-gray-100">{device.serial_number}</div>
                    <span className={cn("mt-1 inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider", badge.pill)}>
                        {badge.label}
                    </span>
                </div>
            </div>

            {/* metric chips */}
            <div className="flex flex-1 flex-wrap items-center gap-x-6 gap-y-2">
                <Metric icon={device.is_charging ? <BatteryCharging className="h-4 w-4 text-amber-500" /> : <Battery className="h-4 w-4" />} label={t('devices.battery', 'Battery')}>
                    {battery != null ? (
                        <span className="inline-flex items-center gap-1.5">
                            <span className="block h-2 w-14 overflow-hidden rounded bg-gray-200 dark:bg-[#33334d]">
                                <span
                                    className={cn("block h-2 rounded", battery > 0.2 ? "bg-emerald-500" : "bg-rose-500")}
                                    style={{ width: `${battery * 100}%` }}
                                />
                            </span>
                            <span className="font-semibold text-gray-700 dark:text-gray-200">{Math.round(battery * 100)}%</span>
                        </span>
                    ) : (
                        <span className="text-gray-400">{t('devices.notReported', 'Not reported')}</span>
                    )}
                </Metric>

                <Metric icon={<Activity className="h-4 w-4" />} label={t('devices.scans24h', 'Scans (24h)')}>
                    <span className="font-semibold text-gray-700 dark:text-gray-200">{scans24.toLocaleString(lang)}</span>
                </Metric>

                <Metric icon={<Clock className="h-4 w-4" />} label={t('devices.lastActivity', 'Last activity')}>
                    <span className="text-gray-700 dark:text-gray-200">
                        {lastActivity ? formatRelativeTime(lastActivity.toISOString(), t, lang) : t('devices.never', 'Never')}
                    </span>
                </Metric>

                <Metric icon={<ShieldCheck className="h-4 w-4" />} label={t('devices.appVersion', 'App Version')}>
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                        {device.app_version ? `v${device.app_version}` : t('devices.notReported', 'Not reported')}
                    </span>
                </Metric>
            </div>

            {/* locate action */}
            <div className="flex-shrink-0">
                {hasGps && displayLat != null && displayLng != null ? (
                    <a
                        href={`https://www.google.com/maps?q=${displayLat},${displayLng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
                    >
                        <MapPin className="h-3.5 w-3.5" />
                        {t('devices.locate', 'Locate')}
                    </a>
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                        <MapPin className="h-3.5 w-3.5" />
                        {t('devices.noGpsData', 'No GPS data')}
                    </span>
                )}
            </div>
        </div>
    );
}

/** Compact labeled metric used inside a device row. */
function Metric({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-2">
            <span className="text-gray-400 dark:text-gray-500">{icon}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-sm leading-none">{children}</span>
        </div>
    );
}

/** Summary stat tile — matches the Checkpoint Directory's StatTile. */
function StatTile({
    icon,
    label,
    value,
    accent = "blue",
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    accent?: "blue" | "emerald";
}) {
    const accentClass =
        accent === "emerald" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-[#3B7BBE]/10 text-[#3B7BBE]";
    return (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
            <span className={cn("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg", accentClass)}>{icon}</span>
            <div className="min-w-0">
                <div className="text-xl font-bold leading-tight text-gray-900 dark:text-white">{value}</div>
                <div className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
            </div>
        </div>
    );
}

function formatRelativeTime(
    dateStr: string,
    t: (key: string, fallback?: string) => string,
    lang: string,
) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t('devices.justNow', 'Just now');
    if (diffMins < 60) return t('devices.minutesAgo', '{n}m ago').replace('{n}', String(diffMins));
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('devices.hoursAgo', '{n}h ago').replace('{n}', String(diffHours));
    return new Intl.DateTimeFormat(lang, { year: "numeric", month: "short", day: "numeric" }).format(date);
}
